import _ from 'lodash';
import moment from 'moment';
import Hashes from 'jshashes';
import * as MODEL from '~/constants/ModelConstants';
import config from '~/config';
import DOMPurify from 'dompurify';

const SHA1 = new Hashes.SHA1();

const formatBreakOn = (char, forceNewLine = false) => {
  return str => {
    if (!str) {
      return { __html: DOMPurify.sanitize(str) };
    }

    let breakChar = !forceNewLine ? '&#x200b;' : '<br />';

    str = str.replace(/</g, '&lt;');
    str = str.replace(/>/g, '&gt;');

    let regString = char === '.' ? '\\.' : char;

    let regExp = new RegExp(regString, 'g');

    return { __html: DOMPurify.sanitize(str.replace(regExp, `${char}${breakChar}`)) };
  };
}

const Helpers = {
  getRepoPath: (repoName) => {
    const repoParts = repoName.split('/');
    repoParts.pop();
    return repoParts.join('/');
  },

  getRepoName: (repoName) => {
    const repoParts = repoName.split('/');
    return repoParts.pop();
  },

  numberWithCommas: (x) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  prettyEnum: (str) => {
    if (!str) {
      return str;
    }
    let result = str
      .replace('REPOS', 'REPOSITORIES')
      //.replace('REPO', 'REPOSITORY')
      .replace('VULN_', 'VULNERABILITY_')
      .split('_')
      .map(s => {
        return this.capFirst(s);
      })
      .join(' ');

    if (result === 'Components') {
      result = 'Libraries';
    }

    return result;
  },

  matchGrab: (str, regex) => {
    if (!str || !str.match || !regex) {
      return '';
    }

    const result = str.match(regex);

    if (result && result.length && result.length === 2) {
      return result[1];
    }

    return '';
  },

  smartRound: (num, maxLength) => {
    if (isNaN(num) || isNaN(maxLength)) {
      return num;
    }

    const decimalPlaces = maxLength + 2;
    let tmpRound;
    let tmpRoundStr;

    for (let i = decimalPlaces; i >= 0; i--) {
      tmpRound = _.round(num, i);
      tmpRoundStr = '' + tmpRound;
      if (tmpRoundStr.length <= maxLength) {
        return tmpRound;
      }
    }

    return num;
  },

  cutString: (tex, len) => {
    return tex.length > len ? tex.substring(0, len) + '...' : tex;
  },

  cut: (str, len) => {
    if (str.length > len) return str.substring(0, len) + '...';
    return str;
  },

  decode: val => {
    if (decodeURIComponent(val) !== val) {
      return decodeURIComponent(val);
    }

    return val;
  },

  formatE2E: str => {
    return str && str.replace
      ? str
          .replace(/\W+/g, '-')
          .replace(/([a-z\d])([A-Z])/g, '$1-$2')
          .toLowerCase()
      : '';
  },

  highlightItems: (content, items, cutLen) => {
    if (!content || typeof items !== 'object') {
      return { __html: DOMPurify.sanitize(content) };
    }

    content = content.replace(/</g, '&lt;');
    content = content.replace(/>/g, '&gt;');

    items = items.map(function(a) {
      return a.replace(/[^A-Za-z0-9_\s-]/g, '');
    });
    items = items.filter(function(a) {
      return a.length > 0;
    });

    if (cutLen && items.length && content && content.length > cutLen) {
      const test = content;
      let testIndex;
      let foundIndex = -1;

      for (let j = 0; j < items.length; j++) {
        testIndex = test.indexOf(items[j].toLowerCase());
        if (items[j].length > 2 && testIndex !== -1) {
          foundIndex = testIndex;
          break;
        }
      }

      if (foundIndex > -1) {
        if (foundIndex > cutLen) {
          if (foundIndex < content.length - cutLen) {
            content = '...' + content.substr(foundIndex - cutLen / 2, cutLen) + '...';
          } else {
            content = '...' + content.substr(cutLen * -1);
          }
        }
      }

      content = Helpers.cut(content, cutLen);
    } else if (cutLen && content && content.length > cutLen) {
      content = Helpers.cut(content, cutLen);
    }

    let html = _.clone(content);

    for (let i = 0; i < items.length; i++) {
      if (items[i].length < 2) {
        continue;
      }
      html = html
        ? html.replace(
            new RegExp('(' + items[i] + ')', 'gi'),
            '<span class="text--highlight-yellow">$1</span>'
          )
        : html;
    }

    return { __html: DOMPurify.sanitize(html) };
  },

  debounce: (func, wait, immediate) => {
    let timeout;
    return function() {
      let context = this,
        args = arguments;
      const later = function() {
        timeout = null;
        if (!immediate) {
          func.apply(context, args);
        }
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) {
        func.apply(context, args);
      }
    };
  },

  sha1: toHash => {
    return SHA1.hex(toHash).slice(0, 9);
  },

  formatDate: dateStr => {
    return dateStr ? moment(dateStr).format('MMM D, YYYY') : 'Unknown';
  },

  formatTimestamp: dateStr => {
    return dateStr ? moment(dateStr).format('MMM D, YYYY HH:MM:ss') : 'Unknown';
  },

  formatDateRel: dateStr => {
    return dateStr ? moment(dateStr).fromNow() : 'Unknown';
  },

  timeSince: (timeStamp, minDaysShowingRelativeTimestamp = 60) => {
    if (!timeStamp) {
      return;
    }

    const now = new Date();
    const secondsPast = (now.getTime() - timeStamp.getTime()) / 1000;
    const oneMinInSec = 60;
    const oneHrInSec = oneMinInSec * 60;
    const oneDayInSec = oneHrInSec * 24;

    if (secondsPast < oneMinInSec) {
      return `${Math.floor(secondsPast)} s ago`;
    }
    if (secondsPast < oneHrInSec) {
      return `${Math.floor(secondsPast / oneMinInSec)} m ago`;
    }
    if (secondsPast <= oneDayInSec) {
      return `${Math.floor(secondsPast / oneHrInSec)} h ago`;
    }
    if (secondsPast <= oneDayInSec * minDaysShowingRelativeTimestamp) {
      return `${Math.floor(secondsPast / oneDayInSec)} d ago`;
    }
    if (secondsPast > oneDayInSec * minDaysShowingRelativeTimestamp) {
      const day = timeStamp.getDate();
      const month = timeStamp
        .toDateString()
        .match(/ [a-zA-Z]*/)[0]
        .replace(' ', '');
      const year = timeStamp.getFullYear() === now.getFullYear() ? '' : timeStamp.getFullYear();
      return `${day} ${month} ${year}`;
    }
  },

  capFirst: str => {
    if (!str) {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  sanitizeLanguageType: languageType => {
    if (!languageType) {
      return '';
    }
    if (languageType.toUpperCase() === 'RUBY') {
      return 'Ruby';
    } else if (languageType.toLowerCase() === 'js' || languageType.toLowerCase() === 'javascript') {
      return 'JavaScript';
    } else if (languageType.toUpperCase() === 'JAVA') {
      return 'Java';
    } else if (languageType.toUpperCase() === 'PYTHON') {
      return 'Python';
    } else if (languageType.toUpperCase() === 'OBJECTIVEC') {
      return 'Objective-C';
    } else if (languageType.toUpperCase() === 'SWIFT') {
      return 'Swift';
    } else if (languageType.toUpperCase() === 'CSHARP') {
      return 'C#';
    } else if (languageType.toUpperCase() === 'CPP') {
      return 'C/C++';
    }
    return languageType || '--';
  },

  sanitizePackageManagerType: packageManagerType => {
    if (!packageManagerType) {
      return '';
    }
    const formattedPackagemanager = MODEL.packageManagerTypeMap[packageManagerType.toLowerCase()];
    return formattedPackagemanager || '';
  },

  getComponentName: comp => {
    return (
      (
        (comp.coordinate1 ? comp.coordinate1 : '') +
        ' ' +
        (comp.coordinate2 ? comp.coordinate2 : '')
      ).trim() +
      ' ' +
      comp.version
    ).trim();
  },

  generateRefId: (id, name, version) => {
    return [
      id ? id : 'nm',
      name.replace(/\W/g, '-').toLowerCase(),
      version.replace(/\./g, '_'),
    ].join('-');
  },

  humanReadableLine: ln => {
    if (ln && ln.indexOf('#L') !== -1) {
      const parts = ln.split('#L');
      return 'Line ' + parts[1] + ' of ' + parts[0];
    }

    return ln;
  },

  isGplString: t => {
    return t && !!(t.match(/\bGPL/i) || t.match(/\(A?GPL/i));
  },
  formatBreakOn: formatBreakOn,

  formatBreakOnSlash: formatBreakOn('/'),

  formatBreakOnDot: formatBreakOn('.'),

  getObjectAsListSortedKeyValue: (data, limitTo) => {
    const keys = Object.keys(data || {});
    let result = keys
      .map(function(key) {
        return { label: key, count: data[key], percentOfMax: 0 };
      })
      .sort(function(a, b) {
        return a.count < b.count ? 1 : -1;
      });

    result = result.map(function(r) {
      r.percentOfMax = (r.count / (result[0].count > 0 ? result[0].count : 1)) * 100;
      return r;
    });

    return result.length > limitTo ? result.slice(0, limitTo) : result;
  },

  isProdEnv: () => {
    return config.API_URL === 'https://api-us-west-2.srcclr.com';
  },

  isCommitHashSupportedByLanguage: language => {
    if (!language) return;
    return MODEL.commitHashSupportedLanguages.includes(language.toUpperCase());
  },

  isCommitHashSupportedByPackageManager: packageManager => {
    if (!packageManager) return;
    return MODEL.commitHashSupportedPackageManagers.includes(packageManager.toUpperCase());
  },

  isCommitHash: s => {
    //http://stackoverflow.com/questions/1896715/how-do-i-check-if-a-string-is-a-valid-md5-or-sha1-checksum-string
    // At this point all commit hash are SHA-1
    if (!s) return;
    return s.match('[a-fA-F0-9]{40}');
  },

  /**
   * Check if the url ends with a platform-frontend expected hostname
   * @param {String} url to check
   */
  isPlatformFrontendUrl: url => {
    if (!url || url.length === 0) {
      return false;
    }

    const urlToCheck = new URL(url);
    let isPlatformUrl = false;

    config.PLATFORM_FRONTEND_DOMAIN.forEach(domain => {
      // Special check for ops2 since they are on the same domain
      // platform is org.ops2.srcclr.io and registry is www.ksp.ops2.srcclr.io
      if (
        urlToCheck.hostname.endsWith(domain) &&
        !urlToCheck.hostname.endsWith('ksp.ops2.srcclr.io')
      ) {
        isPlatformUrl = true;
      }
    });

    return isPlatformUrl;
  },

  /**
   * Check for the team id based on the url. return null if not present
   * @param {String} url to check
   */
  getTeamFromUrl: url => {
    if (Helpers.isPlatformFrontendUrl(url)) {
      const urlPaths = new URL(url).pathname.split('/').filter(path => path.length > 0);

      return urlPaths[0] === 'teams' ? urlPaths[1] : null;
    }

    return null;
  },

  isOnPrem: () => {
    return config.ENVIRONMENT === MODEL.ENVIRONMENT_MAP.ONPREM;
  },

  /**
   * Return a promise that will reject in the duration given
   * @param {int} miliseconds before timeout. 5000 would be 5 secs.
   */
  getTimeOutPromise: ms => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        clearTimeout(timer);
        reject();
      }, ms);
    });
  },

  getFirstNonNull: items => {
    return items.find(item => item != null);
  },

  /**
   * Return query string with a value translated to API compatible value given a compatibleValuesMap
   * @param Object query, targetField, valueToTransform, compatibleValuesMap
   */
  translateQueryValueToApiCompatibleValue: ({
    query,
    targetField,
    valueToTransform,
    compatibleValuesMap,
  }) => {
    const queryArr = query.split(/\s+/);
    let res = query;
    queryArr.forEach((field, index) => {
      if (field) {
        const fieldArr = field.split(':') || [];

        // Only when the target field has value then perform transformation. query is in the form of <targetField>:<valueToTransform>
        if (fieldArr.length > 1 && fieldArr[0].toLowerCase() === targetField.toLowerCase()) {
          // Perform replace of the valueToTransform based on mapping from compatibleValuesMap
          queryArr[index] = `${fieldArr[0]}:${fieldArr[1].replace(
            valueToTransform,
            compatibleValuesMap[targetField][valueToTransform]
          )}`;
          res = queryArr.join(' ');
        }
      }
    });

    return res;
  },

  isVeracodeDomain: () => _.includes(config.VERACODE_DOMAINS, window.location.hostname),
  isVeracodeUser: (state) => {
    const { myState } = state;
    let isVeracodeUser = false;

    if (myState && myState.me && myState.me.roles) {
      const { roles } = myState.me;
      isVeracodeUser = _.includes(roles, 'VERACODE_USER');
    }
    return isVeracodeUser;
  }
};

export default Helpers;
